import dayjs, { type Dayjs } from 'dayjs';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';

import { AttendanceType, type CalendarValues } from './types';
import { calcSessions } from './utils/calcSessions';

export const useCreateCalendarValues = () => {
    const { t } = useTranslation('calendar');

    const schema = yup.object({
        startTime: yup.string().required(t('error-start-time-required')),
        endTime: yup.string().required(t('error-end-time-required')),
        days: yup.array().min(1, t('error-days-required')),
        dateRange: yup
            .array()
            .min(2)
            .required(t('error-date-range-required'))
            .test('atLeastOne', t('error-date-range-must-include-session'), function performTest(value) {
                const { sessions } = calcSessions({
                    startTime: this.parent.startTime,
                    endTime: this.parent.endTime,
                    days: this.parent.days,
                    dateRange: value as Dayjs[],
                    timezone: this.parent.timezone
                });
                return sessions?.length > 0;
            }),
        timezone: yup.string().required(t('error-timezone-required')),
        overnightAcknowledged: yup.bool().when(['startTime', 'endTime', 'days', 'dateRange', 'timezone'], {
            is: (startTime, endTime, days, dateRange, timezone) => {
                const { isOvernight } = calcSessions({ startTime, endTime, days, dateRange, timezone });

                return isOvernight;
            },
            // this makes sure they've acknowledged that they're creating an overnight class
            then: (value) => value.required(t('invalid-class-times')).oneOf([true], t('invalid-class-times'))
        })
    });

    const defaultValues: CalendarValues = {
        days: [] as number[],
        timezone: dayjs.tz.guess(),
        dateRange: [dayjs(), dayjs().add(6, 'days')],
        startTime: dayjs().format('HH:mm'),
        endTime: dayjs().add(1, 'hour').format('HH:mm'),
        label: '',
        selectedGroups: ['all'],
        classSessionGroupUuid: uuid(),
        attendanceType: AttendanceType.Native
    };

    const defaultValuesWithInitialDay: CalendarValues = {
        ...cloneDeep(defaultValues),
        days: [dayjs().day()]
    };

    const returnNewDefaultCalendarValues = () => ({
        defaultValues: {
            ...cloneDeep(defaultValues),
            classSessionGroupUuid: uuid()
        },
        defaultValuesWithInitialDay: {
            ...cloneDeep(defaultValuesWithInitialDay),
            classSessionGroupUuid: uuid()
        }
    });

    return {
        schema,
        defaultValues,
        defaultValuesWithInitialDay,
        returnNewDefaultCalendarValues
    };
};
